import { useEffect, useState, type ReactNode } from "react";
import { LoaderCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@nexus/ui";
import { cn } from "@/lib/utils";

interface FullPageStateProps {
  code?: string;
  title: string;
  description: string;
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  className?: string;
  children?: ReactNode;
}

/**
 * Full-viewport state screen for loading, error, not-found and forbidden states.
 * Replaces inline duplicated patterns in routes.tsx.
 */
export function FullPageState({
  code,
  title,
  description,
  action,
  className,
  children,
}: FullPageStateProps) {
  return (
    <div
      className={cn("min-h-screen flex items-center justify-center bg-background px-6", className)}
    >
      <div className="text-center max-w-sm">
        {code && <p className="text-4xl font-bold text-brand-navy/20 dark:text-white/20">{code}</p>}
        <h1 className={cn("text-lg font-semibold text-primary", code && "mt-2")}>{title}</h1>
        <p className="mt-2 text-sm text-muted">{description}</p>
        {action && (
          <div className="mt-6">
            {action.href ? (
              <Link
                to={action.href}
                className="inline-flex items-center rounded-lg bg-brand-cyan px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-cyan/90"
              >
                {action.label}
              </Link>
            ) : (
              <button
                type="button"
                onClick={action.onClick}
                className="rounded-lg bg-brand-cyan px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-cyan/90"
              >
                {action.label}
              </button>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

/** How long a page may load before it says it is taking longer than usual. */
export const SLOW_LOADING_MS = 10_000;

/**
 * A page that cannot show anything yet (sign-in, account, a route still loading):
 * a progress hairline at the top edge and, centered, a spinner with what is
 * loading. Once it has taken {@link SLOW_LOADING_MS} it says so and offers a
 * reload, so a slow server never looks like a blank, dead page. The widgets and
 * lists of a page that already exists show skeletons instead, with no text.
 */
export function FullPageLoader({ label = "Loading" }: { label?: string }) {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), SLOW_LOADING_MS);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-background px-6"
      role="status"
      aria-live="polite"
    >
      <div className="loading-bar fixed inset-x-0 top-0 h-0.5" aria-hidden="true" />
      {/* The line is what is centered; the slow notice hangs below it without moving it. */}
      <div className="relative">
        <div className="flex items-center gap-2 text-sm text-secondary">
          <LoaderCircle
            className="h-4 w-4 animate-spin text-brand-cyan motion-reduce:animate-none"
            aria-hidden
          />
          {label}…
        </div>
        {slow ? (
          <div className="absolute left-1/2 top-full mt-3 flex w-[min(24rem,calc(100vw-3rem))] -translate-x-1/2 flex-col items-center gap-3 text-center">
            <p className="text-xs text-muted">
              This is taking longer than usual. The server may be starting up or slow to respond.
            </p>
            <Button size="sm" variant="secondary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
